import { type MoneymapItemWithTitle } from "./moneymap";

export const OUT_OF_DEPARTMENT_ZONE_ID = "00000000000000000000000000000000000000000000";

const DEPARTMENTS_ORDER = ["WOMAN", "MAN", "KIDS"];
const SUBZONES_ORDER = ["WALL", "FLOOR", "TABLE"];

export type ZoneMap = Record<string, { title?: string; type?: string } | undefined>;

/**
 * Сортирует элементы по отделам: WOMAN, MAN, KIDS, затем вне отдела.
 *
 * @param data - массив элементов MoneymapItemWithTitle
 * @returns новый отсортированный массив (исходный не изменяется)
 */
export function sortMoneymapByDepartments(data: MoneymapItemWithTitle[]) {
  const orderOf = (item: MoneymapItemWithTitle): number => {
    const idx = DEPARTMENTS_ORDER.indexOf(item.title ?? "");

    return idx === -1 ? DEPARTMENTS_ORDER.length + 1 : idx;
  };

  return [...data].sort((a, b) => orderOf(a) - orderOf(b));
}

const OUT_OF_DEPARTMENT_ORDER = SUBZONES_ORDER.length + 1;

/**
 * Сортирует элементы по подзонам: WALL, FLOOR, TABLE (по началу title), остальное по алфавиту,
 * в конце — зона с zone_id === OUT_OF_DEPARTMENT_ZONE_ID.
 *
 * @param data - массив элементов MoneymapItemWithTitle
 * @returns новый отсортированный массив (исходный не изменяется)
 */
export function sortMoneymapBySubzones(data: MoneymapItemWithTitle[]) {
  const getTitle = (item: MoneymapItemWithTitle) => (item.title ?? "").trim();

  const orderOf = (item: MoneymapItemWithTitle): number => {
    if (item.zone_id === OUT_OF_DEPARTMENT_ZONE_ID) return OUT_OF_DEPARTMENT_ORDER;

    const title_upper = getTitle(item).toUpperCase();
    const order_idx = SUBZONES_ORDER.findIndex((z) => title_upper.startsWith(z));

    return order_idx === -1 ? SUBZONES_ORDER.length : order_idx;
  };

  /** Единый ключ для стабильной сортировки: приоритет (00–04), затем title (алфавит внутри группы). */
  const sortKey = (item: MoneymapItemWithTitle) => `${orderOf(item).toString().padStart(2, "0")}_${getTitle(item)}`;

  return [...data].sort((a, b) => sortKey(a).localeCompare(sortKey(b), undefined, { numeric: true }));
}

/**
 * Сортирует элементы по убыванию поля amount_total (числовое сравнение).
 *
 * @param data - массив элементов MoneymapItemWithTitle
 * @returns новый отсортированный массив (исходный не изменяется)
 */
export function sortMoneymapByAmount(data: MoneymapItemWithTitle[]) {
  return [...data].sort((a, b) => Number(b.amount_total) - Number(a.amount_total));
}

/**
 * Сортирует элементы по убыванию поля quantity_total.
 *
 * @param data - массив элементов MoneymapItemWithTitle
 * @returns новый отсортированный массив (исходный не изменяется)
 */
export function sortMoneymapByQuantity(data: MoneymapItemWithTitle[]) {
  return [...data].sort((a, b) => b.quantity_total - a.quantity_total);
}

/**
 * Режим сортировки элементов карты денег.
 * - default — по подзонам (sortMoneymapBySubzones)
 * - amount — по убыванию amount_total
 * - quantity — по убыванию quantity_total
 */
export type MoneymapSortMode = "default" | "amount" | "quantity";

/**
 * Сортирует массив элементов по выбранному режиму.
 *
 * @param data - массив элементов MoneymapItemWithTitle
 * @param mode - режим: "default" (подзоны), "amount" (по сумме), "quantity" (по количеству)
 * @returns новый отсортированный массив (исходный не изменяется)
 */
export function sortMoneymap(data: MoneymapItemWithTitle[], mode: MoneymapSortMode) {
  switch (mode) {
    case "amount":
      return sortMoneymapByAmount(data);
    case "quantity":
      return sortMoneymapByQuantity(data);
    default:
      return sortMoneymapBySubzones(data);
  }
}
